import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { listCases, getCase, createCase, updateCase, deleteCase, addCaseEvent, listDocuments } from '../services/api';
import toast from 'react-hot-toast';

const JURISDICTIONS = ['HK', 'UK', 'US', 'SG', 'AU', 'CN', 'Other'];
const STATUS_COLOR: Record<string, string> = {
  active: 'text-green-700 bg-green-50 border-green-200',
  pending: 'text-amber-700 bg-amber-50 border-amber-200',
  closed: 'text-gray-500 bg-gray-50 border-gray-200',
};
const EVENT_ICON: Record<string, string> = {
  filing: '📋', hearing: '🏛', deadline: '⏰', note: '📝', milestone: '🎯',
};

export default function CasesPage() {
  const { currentUser } = useAuth();
  const [cases, setCases] = useState<any[]>([]);
  const [selectedCase, setSelectedCase] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [docs, setDocs] = useState<any[]>([]);

  // Create form
  const [newCase, setNewCase] = useState({ title: '', description: '', jurisdiction: 'HK', related_doc_ids: [] as string[] });
  const [creating, setCreating] = useState(false);

  // Event form
  const [showEventForm, setShowEventForm] = useState(false);
  const [newEvent, setNewEvent] = useState({ description: '', date: '', event_type: 'note' });
  const [addingEvent, setAddingEvent] = useState(false);

  const load = useCallback(async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const [casesRes, docsRes] = await Promise.allSettled([
        listCases(token),
        listDocuments(token),
      ]);
      if (casesRes.status === 'fulfilled') setCases(casesRes.value.cases || []);
      if (docsRes.status === 'fulfilled') setDocs(docsRes.value.documents || []);
    } catch { /* ok */ }
    finally { setLoading(false); }
  }, [currentUser]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!currentUser || !newCase.title.trim()) return;
    setCreating(true);
    try {
      const token = await currentUser.getIdToken();
      await createCase(token, newCase);
      toast.success('Case created!');
      setShowCreate(false);
      setNewCase({ title: '', description: '', jurisdiction: 'HK', related_doc_ids: [] });
      load();
    } catch (e: any) { toast.error(e.message || 'Failed'); }
    finally { setCreating(false); }
  };

  const handleSelectCase = async (caseId: string) => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await getCase(token, caseId);
      setSelectedCase(res.case);
    } catch (e: any) { toast.error(e.message || 'Failed to load case'); }
  };

  const handleStatusChange = async (caseId: string, status: string) => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      await updateCase(token, caseId, { status });
      toast.success(`Status updated to ${status}`);
      // Refresh
      handleSelectCase(caseId);
      load();
    } catch (e: any) { toast.error(e.message || 'Failed'); }
  };

  const handleDelete = async (caseId: string, title: string) => {
    if (!currentUser || !window.confirm(`Delete case "${title}"?`)) return;
    try {
      const token = await currentUser.getIdToken();
      await deleteCase(token, caseId);
      toast.success('Case deleted');
      setSelectedCase(null);
      load();
    } catch (e: any) { toast.error(e.message || 'Failed'); }
  };

  const handleAddEvent = async () => {
    if (!currentUser || !selectedCase || !newEvent.description.trim()) return;
    setAddingEvent(true);
    try {
      const token = await currentUser.getIdToken();
      await addCaseEvent(token, selectedCase.case_id, newEvent);
      toast.success('Event added!');
      setShowEventForm(false);
      setNewEvent({ description: '', date: '', event_type: 'note' });
      handleSelectCase(selectedCase.case_id);
    } catch (e: any) { toast.error(e.message || 'Failed'); }
    finally { setAddingEvent(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading cases...</div>;

  return (
    <div className="max-w-5xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Case Tracker</h1>
          <p className="text-gray-500 text-sm mt-1">Manage legal matters, track deadlines, and log events.</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="px-5 py-2.5 bg-blue-700 text-white rounded-lg text-sm font-semibold hover:bg-blue-600 transition-colors">
          + New Case
        </button>
      </div>

      {/* Create case modal */}
      {showCreate && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
          <h2 className="font-semibold text-gray-800">Create New Case</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
              <input type="text" value={newCase.title} onChange={(e) => setNewCase((c) => ({ ...c, title: e.target.value }))}
                placeholder="e.g. Smith v Jones — Employment Dispute"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <textarea value={newCase.description} onChange={(e) => setNewCase((c) => ({ ...c, description: e.target.value }))}
                placeholder="Brief description of the matter..."
                rows={3}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Jurisdiction</label>
              <select value={newCase.jurisdiction} onChange={(e) => setNewCase((c) => ({ ...c, jurisdiction: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                {JURISDICTIONS.map((j) => <option key={j} value={j}>{j}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Related Documents</label>
              <select multiple value={newCase.related_doc_ids}
                onChange={(e) => setNewCase((c) => ({ ...c, related_doc_ids: Array.from(e.target.selectedOptions, (o) => o.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 h-20">
                {docs.map((d) => <option key={d.doc_id} value={d.doc_id}>{d.label}</option>)}
              </select>
              <p className="text-xs text-gray-400 mt-1">Hold Ctrl/Cmd to select multiple</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleCreate} disabled={creating || !newCase.title.trim()}
              className="px-5 py-2.5 bg-blue-700 text-white rounded-lg text-sm font-semibold hover:bg-blue-600 disabled:opacity-50 transition-colors">
              {creating ? 'Creating...' : 'Create Case'}
            </button>
            <button onClick={() => setShowCreate(false)}
              className="px-5 py-2.5 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="flex gap-6">
        {/* Case list */}
        <div className="w-72 flex-shrink-0 space-y-2">
          {cases.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">
              <div className="text-3xl mb-2">📁</div>
              <p>No cases yet</p>
            </div>
          ) : (
            cases.map((c) => (
              <button key={c.case_id} onClick={() => handleSelectCase(c.case_id)}
                className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${
                  selectedCase?.case_id === c.case_id ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200 hover:border-blue-200'
                }`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-semibold text-gray-800 truncate flex-1">{c.title}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${STATUS_COLOR[c.status] || STATUS_COLOR.active}`}>
                    {c.status}
                  </span>
                  <span className="text-xs text-gray-400">{c.jurisdiction}</span>
                  <span className="text-xs text-gray-400 ml-auto">{c.event_count || 0} events</span>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Case detail */}
        <div className="flex-1">
          {!selectedCase ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center text-gray-400">
              <div className="text-4xl mb-3">📁</div>
              <p className="text-sm">Select a case to view details</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Case header */}
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">{selectedCase.title}</h2>
                    <p className="text-xs text-gray-500 mt-1">{selectedCase.jurisdiction} · Created {selectedCase.created_at?.slice(0, 10)}</p>
                  </div>
                  <div className="flex gap-2">
                    {(['active', 'pending', 'closed'] as const).map((s) => (
                      <button key={s} onClick={() => handleStatusChange(selectedCase.case_id, s)}
                        className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${
                          selectedCase.status === s ? STATUS_COLOR[s] : 'border-gray-200 text-gray-400 hover:border-gray-400'
                        }`}>
                        {s}
                      </button>
                    ))}
                    <button onClick={() => handleDelete(selectedCase.case_id, selectedCase.title)}
                      className="text-xs px-3 py-1.5 border border-gray-200 text-gray-400 rounded-full hover:text-red-600 hover:border-red-300 transition-colors">
                      Delete
                    </button>
                  </div>
                </div>
                {selectedCase.description && (
                  <p className="text-sm text-gray-600 leading-relaxed">{selectedCase.description}</p>
                )}
              </div>

              {/* Add event */}
              <div className="flex items-center gap-2">
                <button onClick={() => setShowEventForm(!showEventForm)}
                  className="px-4 py-2 bg-emerald-700 text-white rounded-lg text-xs font-medium hover:bg-emerald-600 transition-colors">
                  + Add Event
                </button>
              </div>

              {showEventForm && (
                <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
                      <input type="date" value={newEvent.date}
                        onChange={(e) => setNewEvent((ev) => ({ ...ev, date: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Event Type</label>
                      <select value={newEvent.event_type}
                        onChange={(e) => setNewEvent((ev) => ({ ...ev, event_type: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="note">Note</option>
                        <option value="filing">Filing</option>
                        <option value="hearing">Hearing</option>
                        <option value="deadline">Deadline</option>
                        <option value="milestone">Milestone</option>
                      </select>
                    </div>
                    <div className="md:col-span-1">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                      <input type="text" value={newEvent.description}
                        onChange={(e) => setNewEvent((ev) => ({ ...ev, description: e.target.value }))}
                        placeholder="What happened or needs to happen?"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={handleAddEvent} disabled={addingEvent || !newEvent.description.trim()}
                      className="px-4 py-2 bg-emerald-700 text-white rounded-lg text-xs font-medium hover:bg-emerald-600 disabled:opacity-50 transition-colors">
                      {addingEvent ? 'Adding...' : 'Add Event'}
                    </button>
                    <button onClick={() => setShowEventForm(false)}
                      className="px-4 py-2 border border-gray-300 text-gray-500 rounded-lg text-xs hover:bg-gray-50 transition-colors">
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Timeline */}
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-800 mb-4">Timeline</h3>
                {(!selectedCase.events || selectedCase.events.length === 0) ? (
                  <p className="text-sm text-gray-400 text-center py-6">No events yet — add one above</p>
                ) : (
                  <div className="relative">
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
                    <div className="space-y-4">
                      {selectedCase.events.map((ev: any, i: number) => (
                        <div key={ev.event_id || i} className="relative pl-10">
                          <div className="absolute left-2.5 w-3 h-3 rounded-full bg-white border-2 border-blue-500 mt-1.5" />
                          <div className="bg-gray-50 rounded-xl p-3">
                            <div className="flex items-center gap-2 mb-1">
                              <span>{EVENT_ICON[ev.event_type] || '📝'}</span>
                              <span className="text-xs font-medium text-gray-500 capitalize">{ev.event_type}</span>
                              <span className="text-xs text-gray-400 ml-auto">{ev.date}</span>
                            </div>
                            <p className="text-sm text-gray-700">{ev.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
